import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { NoveltyCheckReportContext } from '../novelty-check-report-context';
import type { NoveltyCheckReportState } from '../novelty-check-report-state';
import { Emitter, Logger, type Event } from '@patsnap/tahiti-core';
import { NoveltyCheckTaskStep } from '@/domains/novelty-check-report/enums';
import { NoveltyCheckTaskStatusModel } from '@/domains/novelty-check-report/models/novelty-check-task-status-model';
import { ReportResultRefresh } from '../../report-result-refresh';
import { NoveltyCheckTaskStatusService } from '../../task-status-service';
import { NoveltyCheckStepInfoStatus } from '@/domains/novelty-check-report/types';
import type { ReportGenerationStatusService } from '../../report-generation-status-service';

/**
 * 查新状态机的报告结果状态。
 *
 * 该状态将 `ReportResultRefresh.refresh()` 成功完成作为报告真正可阅读的边界，
 * 并通过 `ReportGenerationStatusService` 向 onboarding 等外围功能发布完成信号。
 */
@injectable()
export class ResultState implements NoveltyCheckReportState, ReportGenerationStatusService {
    /** 当前报告数据是否已经成功刷新完成。 */
    private _isReportGenerated = false;
    /** 报告刷新完成事件的内部发射器。 */
    private readonly _onReportGeneratedEmitter = new Emitter<void>();

    /** 报告数据刷新完成事件。 */
    readonly onReportGenerated: Event<void> = this._onReportGeneratedEmitter.event;

    // eslint-disable-next-line max-params
    constructor(
        /** 记录报告状态切换和生成过程日志。 */
        @inject(Logger)
        private readonly _logger: Logger,
        /** 拉取并更新最终报告数据。 */
        @inject(ReportResultRefresh)
        private readonly _reportResultRefresh: ReportResultRefresh,
        /** 保存当前查新任务步骤，供页面状态展示。 */
        @inject(NoveltyCheckTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: NoveltyCheckTaskStatusModel,
        /** 进入结果状态前刷新服务端任务状态。 */
        @inject(NoveltyCheckTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: NoveltyCheckTaskStatusService,
    ) { }

    /** 报告数据是否已经刷新完成。 */
    get isReportGenerated(): boolean {
        return this._isReportGenerated;
    }

    /**
     * 进入结果状态，刷新报告并在成功后发布报告生成完成事件。
     * @param context 查新报告状态机上下文。
     */
    async enter(context: NoveltyCheckReportContext): Promise<void> {
        this._isReportGenerated = false;
        await this._noveltyCheckTaskStatusService.pullTaskStatus();
        this._logger.info('进入结果状态');
        this._switchTaskStep(NoveltyCheckTaskStep.REPORT_CREATED);
        this._logger.info('开始生成报告');
        // 必须等待报告数据刷新成功，避免外围功能在报告可阅读前展示完成提示。
        await this._reportResultRefresh.refresh();
        this._logger.info('生成报告完成');
        this._isReportGenerated = true;
        this._onReportGeneratedEmitter.fire();
        // 延迟离开当前状态，确保本轮完成事件先同步通知所有订阅方。
        setTimeout(() => {
            context.setStepStatus(this, NoveltyCheckStepInfoStatus.LEAVING);
            context.setStepStatus(this, NoveltyCheckStepInfoStatus.LEFT);
        }, 0);
    }

    /** 处理下一步操作；结果状态已经是流程终点，因此仅记录日志。 */
    async handleNext(): Promise<void> {
        this._logger.info('结果状态是最后一步，没有下一步');
    }

    /**
     * 返回特征对比表生成状态。
     * @param context 查新报告状态机上下文。
     */
    async handlePrevious(context: NoveltyCheckReportContext): Promise<void> {
        /** 状态机中负责生成特征对比表的前置状态。 */
        const generateFeatureCompareTableState = context.getState(NoveltyCheckTaskStep.GENERATE_FEATURE_COMPARE_TABLE);
        await context.transitionTo(generateFeatureCompareTableState);
    }

    /**
     * 同步任务状态模型中的当前步骤。
     * @param step 需要切换到的查新任务步骤。
     */
    private _switchTaskStep(step: NoveltyCheckTaskStep): void {
        this._noveltyCheckTaskStatusModel.update(state => {
            state.taskStep = step;
            return state;
        });
    }
}
